import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@shared/ui-react/Button';
import { Notify } from '@shared/ui-native/Notify';
import { DocumentEditForm } from '@shared/documents/ui/DocumentEditForm';
import { DocumentsImagesStoreAbstract } from '@shared/documents-images/DocumentsImagesStoreAbstract';
import { Drawer } from '@shared/ui-native/Drawer';
import { Events, EventsGlobal } from '@shared/Events';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { DocumentsService } from '@shared/documents/DocumentsService';
import { useCtrlASelect } from '@shared/ui-react/ctrl-a-select-handler';
import { MapWrapper } from '@openlayers/MapWrapper';

import { MapToolsStore } from '@openlayers/MapToolsStore';
import { DialogUtils } from '@shared/ui-react/Dialog';
import { SelectionMapMarkers } from '@openlayers/SelectionMapMarkers';

export const MapDocumentsEditor = {
    openDrawer(
        documents: IDocument[] = [],
        store: DocumentsImagesStoreAbstract<IDocument>,
        treeData: ITreeData,
        mapWrapper: MapWrapper,
        mapTools: MapToolsStore,
        activateEditLocation = false
    ) {
        if (!kuura.hasPrivilege('PROJECT_W')) return;

        const drawerElement = Drawer.open();
        Events.trigger('drawer-open.editor');

        ReactUtils.safeUnmountComponentAtNode(drawerElement);
        ReactUtils.safeRender(
            <MapDocumentsEditorForm
                documents={documents}
                store={store}
                treeData={treeData}
                mapWrapper={mapWrapper}
                mapTools={mapTools}
                activateEditLocation={activateEditLocation}
            />,
            drawerElement
        );
    },

    close() {
        Drawer.close();
        Events.trigger('drawer-close.editor');
        DialogUtils.close();
    },
};

export const MapDocumentsEditorForm: React.FC<{
    treeData: ITreeData;
    documents: IDocument[];
    store: DocumentsImagesStoreAbstract<IDocument>;
    onSuccess?: (data: any) => void;
    mapWrapper?: MapWrapper;
    mapTools: MapToolsStore;
    activateEditLocation: boolean;
}> = ({ treeData, documents, store, onSuccess, mapWrapper, mapTools, activateEditLocation }) => {
    const mapMarkers = mapWrapper ? useMemo(() => new SelectionMapMarkers(mapWrapper), []) : null;
    const firstDocument = documents[0];
    const isBulk = documents?.length > 1;
    const isExternal = !isBulk && documents[0].hasOwnProperty('url');
    const [isLoading, setIsLoading] = useState(false);

    const form = useForm<any>({
        mode: 'all',
        reValidateMode: 'onChange',
        values: firstDocument
            ? {
                  ...firstDocument,
                  latLon: [firstDocument.geographicPoint?.lat ?? null, firstDocument.geographicPoint?.lon ?? null],
              }
            : {},
    });
    const fieldValue = form.watch();
    const allDisabled = isBulk && !fieldValue.locationEnabled && !fieldValue.folderEnabled && !fieldValue.nameEnabled && !fieldValue.descEnabled && !fieldValue.iconEnabled;

    useEffect(() => {
        mapMarkers.drawMarkersDestinationLines(documents ?? []);
        mapMarkers.showDestinationLines(!isBulk); // in bulk mode, disable destination lines until location checkbox is checked

        return () => {
            mapMarkers.destroy();
        };
    }, []);

    useEffect(() => {
        if (fieldValue.latLon) {
            mapMarkers.setDestinationLinesTarget(fieldValue.latLon[0], fieldValue.latLon[1]);
        } else {
            mapMarkers.showDestinationLines(false);
        }
    }, [fieldValue.latLon, fieldValue.locationEnabled]);

    const onSubmit = data => {
        setIsLoading(true);
        const documentIds = documents.filter(d => !d.url).map(i => i.id);
        const externalIds = documents.filter(d => !!d.url).map(i => i.id);

        const formData: any = {
            documentIds,
            externalIds,
            ...data,
        };

        if (isBulk) {
            if (!formData.folderEnabled) {
                delete formData.folder;
                delete formData.folderId;
                delete formData.folderUuid;
            }
            if (!formData.locationEnabled) {
                delete formData.latLon;
                delete formData.geographicPoint;
                delete formData.lat;
                delete formData.lon;
            }
            if (!formData.descEnabled) {
                delete formData.description;
            }
            if (!formData.nameEnabled) {
                delete formData.name;
            }
            if (!formData.iconEnabled) {
                delete formData.icon;
            }
        }
        if (formData.hasOwnProperty('latLon')) {
            formData.lat = formData.latLon[0] === '' ? 0 : formData.latLon[0];
            formData.lon = formData.latLon[1] === '' ? 0 : formData.latLon[1];
        }

        DocumentsService.bulkSave(formData)
            .always(() => setIsLoading(false))
            .fail(Notify.error)
            .done(resp => {
                console.log('formData:', formData);
                if (formData.hasOwnProperty('lat') && formData.hasOwnProperty('lon')) {
                    formData.geographicPoint = {
                        lat: formData.lat === '' ? 0 : formData.lat,
                        lon: formData.lon === '' ? 0 : formData.lon,
                    };
                }

                if (formData.folderId) {
                    formData.folder = { id: formData.folderId };
                }

                store.mergeItems(resp.updatedUuids, formData);
                if (mapWrapper) {
                    mapWrapper.featureLayers.documents.redraw();
                }
                const updatedItems = store.getByUuids(resp.updatedUuids);
                Notify.success();

                setTimeout(() => MapDocumentsEditor.openDrawer(updatedItems, store, treeData, mapWrapper, mapTools), 100);
            });
    };

    const onDelete = () => {
        const promise = store.remove(documents.map(i => i.uuid));

        if (promise) {
            promise
                .always(() => setIsLoading(false))
                .fail(Notify.error)
                .done(resp => {
                    MapDocumentsEditor.close();
                });
        }
    };

    if (!documents || documents.length === 0) {
        return (
            <div className='text-center' style={{ marginTop: 100, padding: 30 }}>
                <h3>No selected documents / Folder is empty</h3>
                <button onClick={MapDocumentsEditor.close}>{__('general.close')}</button>
            </div>
        );
    }

    useCtrlASelect('map-documents-editor');

    return (
        <div id='map-documents-editor'>
            <header>
                <div className='padded'>
                    <h2 style={{ marginBottom: 0 }}>
                        {kuura.getMessage('general.selected_x')}: {documents.length}
                    </h2>
                </div>
            </header>

            <main className='flex-column'>
                <FormProvider {...form}>
                    <DocumentEditForm
                        treeData={treeData}
                        isBulk={isBulk}
                        hasUrl={isExternal}
                        mapWrapper={mapWrapper}
                        mapTools={mapTools}
                        activateEditLocation={activateEditLocation}
                    />
                </FormProvider>
            </main>

            <footer className='flex-row centered'>
                <button onClick={onDelete} className='btn-danger'>
                    <i className='iki iki-trash'></i>
                </button>
                <Button className='ik-button-secondary--subtle' onClick={MapDocumentsEditor.close}>
                    {__('general.close')}
                </Button>
                <Button
                    className='ik-button-primary right'
                    style={{ marginLeft: 'auto' }}
                    loading={isLoading}
                    disabled={/*!form.formState.isDirty || doesn't work correctly within context */ !form.formState.isValid || allDisabled || isLoading}
                    onClick={form.handleSubmit(onSubmit)}
                >
                    {__('general.save')}
                </Button>
            </footer>
        </div>
    );
};
